import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, PaperPlaneTilt } from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Defined OUTSIDE the component to prevent re-mounting on every render,
// which was causing the mobile "screen jumping" bug.
const RadioOption = ({ name, value, label, selected, onChange }) => (
  <label className={`px-4 py-2 rounded-lg border-2 cursor-pointer text-sm font-medium transition ${selected === value ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}>
    <input type="radio" name={name} value={value} checked={selected === value} onChange={() => onChange(name, value)} className="sr-only" />
    {label}
  </label>
);

const LORDForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1=SectionA, 2=SectionB, 3=SectionC

  const [formData, setFormData] = useState({
    project_type: '',
    department: '',
    decision_maker_identified: 'Yes',
    decision_maker_name: '',
    influencers: '',
    lead_source: '',
    interactions: '',
    last_interaction_date: '',
    lost_date: new Date().toISOString().split('T')[0],
    q1_order_went_to: '',
    q1_details: '',
    q2_why_chose_them: '',
    q2_details: '',
    q3_pricing_competitiveness: '',
    q3_comments: '',
    q4_requirement_understanding: '',
    q4_missed_points: '',
    q5_engagement_till_final: '',
    q5_explain: '',
    q6_fight_till_last: '',
    q6_improvement: '',
    q7_why_let_go: '',
    q8_finalized_without_notice: '',
    q8_reason: '',
    q9_relationship_strength: '',
    q10_improvement_areas: [],
    q10_suggestions: '',
    key_reason_for_loss: '',
    what_differently_next_time: '',
  });

  useEffect(() => { fetchLead(); }, [id]);

  const fetchLead = async () => {
    try {
      const resp = await axios.get(`${API}/leads/${id}/detail`);
      setLead(resp.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleImprovement = (area) => {
    setFormData(prev => ({
      ...prev,
      q10_improvement_areas: prev.q10_improvement_areas.includes(area)
        ? prev.q10_improvement_areas.filter(a => a !== area)
        : [...prev.q10_improvement_areas, area]
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/${id}/order-lost`, formData);
      navigate(`/leads/${id}`, { replace: true });
    } catch (error) {
      notify(error.response?.data?.detail || 'Error submitting LORD form');
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full px-4 py-2.5 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none bg-white text-sm";
  const labelCls = "block text-sm font-medium text-[#3B5A54] mb-1.5";
  const radioGroupCls = "flex flex-wrap gap-2";

  if (loading) return <PageSkeleton />;
  if (!lead) return <div className="flex items-center justify-center h-full">Lead not found</div>;

  const canProceed = () => {
    if (step === 1) return formData.project_type && formData.department && formData.lead_source && formData.lost_date;
    if (step === 2) return formData.q1_order_went_to && formData.q2_why_chose_them && formData.q3_pricing_competitiveness && formData.q4_requirement_understanding && formData.q5_engagement_till_final && formData.q6_fight_till_last && formData.q7_why_let_go && formData.q8_finalized_without_notice && formData.q9_relationship_strength;
    if (step === 3) return formData.key_reason_for_loss && formData.what_differently_next_time;
    return false;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="lord-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(`/leads/${id}`)} className="w-10 h-10 flex items-center justify-center text-[#00a69c] hover:bg-[#E2F3F0] rounded-lg transition" data-testid="lord-back-button">
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Lost Order Review</h1>
          <p className="text-[#6D8E87] text-sm">{lead.name} - {lead.company}</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-3 mb-8" data-testid="lord-steps">
        {[{ n: 1, label: 'Section A' }, { n: 2, label: 'Section B' }, { n: 3, label: 'Section C' }].map(({ n, label }) => (
          <div key={n} className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= n ? 'bg-[#00a69c] text-white' : 'bg-[#D5E8E4] text-[#6D8E87]'}`}>{n}</div>
            <span className={`text-sm font-medium ${step >= n ? 'text-[#00a69c]' : 'text-[#6D8E87]'}`}>{label}</span>
            {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? 'bg-[#00a69c]' : 'bg-[#D5E8E4]'}`} />}
          </div>
        ))}
      </div>

      {/* SECTION A: Lead Info + Extra Context */}
      {step === 1 && (
        <div className="medical-card p-6 space-y-5" data-testid="lord-section-a">
          <h2 className="text-xl font-bold text-[#052A23] border-b border-[#D5E8E4] pb-3" style={{ fontFamily: 'Outfit' }}>Section A: Lead & Project Details</h2>

          {/* Auto-filled lead info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-[#F4F9F7] rounded-lg">
            {[
              { label: 'Customer', value: lead.company },
              { label: 'Contact', value: lead.name },
              { label: 'Location', value: `${lead.city}, ${lead.state}` },
              { label: 'Est. Value', value: `₹${(lead.lead_value || 0).toLocaleString('en-IN')}` },
            ].map((item, i) => (
              <div key={i}><p className="text-xs text-[#6D8E87]">{item.label}</p><p className="text-sm font-medium text-[#052A23]">{item.value || '-'}</p></div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Project Type *</label>
              <select value={formData.project_type} onChange={e => set('project_type', e.target.value)} className={inputCls} data-testid="lord-project-type">
                <option value="">Select</option>
                {['New Setup', 'Expansion', 'Replacement', 'Renovation', 'Tender/Bid'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Department *</label>
              <input value={formData.department} onChange={e => set('department', e.target.value)} className={inputCls} placeholder="e.g., ICU, OT, General Ward" data-testid="lord-department" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Decision Maker Identified? *</label>
              <div className={radioGroupCls}>
                <RadioOption name="decision_maker_identified" value="Yes" label="Yes" selected={formData.decision_maker_identified} onChange={set} />
                <RadioOption name="decision_maker_identified" value="No" label="No" selected={formData.decision_maker_identified} onChange={set} />
              </div>
            </div>
            {formData.decision_maker_identified === 'Yes' && (
              <div>
                <label className={labelCls}>Decision Maker Name</label>
                <input value={formData.decision_maker_name} onChange={e => set('decision_maker_name', e.target.value)} className={inputCls} placeholder="Name" data-testid="lord-dm-name" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Key Influencers</label>
              <input value={formData.influencers} onChange={e => set('influencers', e.target.value)} className={inputCls} placeholder="People who influenced the decision" data-testid="lord-influencers" />
            </div>
            <div>
              <label className={labelCls}>Lead Source *</label>
              <select value={formData.lead_source} onChange={e => set('lead_source', e.target.value)} className={inputCls} data-testid="lord-lead-source">
                <option value="">Select</option>
                {['Direct Inquiry', 'Referral', 'Tender Notice', 'Trade Show', 'Cold Call', 'Website', 'Other'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Total Interactions / Visits</label>
            <input value={formData.interactions} onChange={e => set('interactions', e.target.value)} className={inputCls} placeholder="e.g., 5 visits, 3 demos" data-testid="lord-interactions" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Last Interaction Date</label>
              <input type="date" value={formData.last_interaction_date} onChange={e => set('last_interaction_date', e.target.value)} className={inputCls} data-testid="lord-last-interaction" />
            </div>
            <div>
              <label className={labelCls}>Date of Loss *</label>
              <input type="date" value={formData.lost_date} onChange={e => set('lost_date', e.target.value)} className={inputCls} data-testid="lord-lost-date" />
            </div>
          </div>
        </div>
      )}

      {/* SECTION B: Lost Order Analysis (10 Questions) */}
      {step === 2 && (
        <div className="medical-card p-6 space-y-6" data-testid="lord-section-b">
          <h2 className="text-xl font-bold text-[#052A23] border-b border-[#D5E8E4] pb-3" style={{ fontFamily: 'Outfit' }}>Section B: Lost Order Analysis</h2>

          {/* Q1 */}
          <div className="space-y-2">
            <label className={labelCls}>Q1. Who did the order go to? *</label>
            <input value={formData.q1_order_went_to} onChange={e => set('q1_order_went_to', e.target.value)} className={inputCls} placeholder="Competitor name" data-testid="lord-q1" />
            <input value={formData.q1_details} onChange={e => set('q1_details', e.target.value)} className={inputCls} placeholder="Additional details (optional)" />
          </div>

          {/* Q2 */}
          <div className="space-y-2">
            <label className={labelCls}>Q2. Why did the customer choose them? *</label>
            <div className={radioGroupCls}>
              {['Lower Price', 'Better Product', 'Better Relationship', 'Brand Preference', 'Faster Delivery', 'Other'].map(o => (
                <RadioOption key={o} name="q2_why_chose_them" value={o} label={o} selected={formData.q2_why_chose_them} onChange={set} />
              ))}
            </div>
            <input value={formData.q2_details} onChange={e => set('q2_details', e.target.value)} className={inputCls} placeholder="Elaborate (optional)" />
          </div>

          {/* Q3 */}
          <div className="space-y-2">
            <label className={labelCls}>Q3. How competitive was our pricing? *</label>
            <div className={radioGroupCls}>
              {['Much Higher', 'Slightly Higher', 'At Par', 'Lower', 'Not Compared'].map(o => (
                <RadioOption key={o} name="q3_pricing_competitiveness" value={o} label={o} selected={formData.q3_pricing_competitiveness} onChange={set} />
              ))}
            </div>
            <input value={formData.q3_comments} onChange={e => set('q3_comments', e.target.value)} className={inputCls} placeholder="Price comments (optional)" />
          </div>

          {/* Q4 */}
          <div className="space-y-2">
            <label className={labelCls}>Q4. Did we understand the requirement fully? *</label>
            <div className={radioGroupCls}>
              {['Fully Understood', 'Partially Understood', 'Misunderstood', 'Not Sure'].map(o => (
                <RadioOption key={o} name="q4_requirement_understanding" value={o} label={o} selected={formData.q4_requirement_understanding} onChange={set} />
              ))}
            </div>
            <input value={formData.q4_missed_points} onChange={e => set('q4_missed_points', e.target.value)} className={inputCls} placeholder="What points were missed? (optional)" />
          </div>

          {/* Q5 */}
          <div className="space-y-2">
            <label className={labelCls}>Q5. Were we engaged till the final decision? *</label>
            <div className={radioGroupCls}>
              {['Yes, till the end', 'Partially', 'Lost touch before decision', 'Never fully engaged'].map(o => (
                <RadioOption key={o} name="q5_engagement_till_final" value={o} label={o} selected={formData.q5_engagement_till_final} onChange={set} />
              ))}
            </div>
            <input value={formData.q5_explain} onChange={e => set('q5_explain', e.target.value)} className={inputCls} placeholder="Explain (optional)" />
          </div>

          {/* Q6 */}
          <div className="space-y-2">
            <label className={labelCls}>Q6. Did we fight till the last? *</label>
            <div className={radioGroupCls}>
              {['Yes, gave our best', 'Could have done more', 'Gave up early', 'Was not aware it was closing'].map(o => (
                <RadioOption key={o} name="q6_fight_till_last" value={o} label={o} selected={formData.q6_fight_till_last} onChange={set} />
              ))}
            </div>
            <input value={formData.q6_improvement} onChange={e => set('q6_improvement', e.target.value)} className={inputCls} placeholder="What could be improved? (optional)" />
          </div>

          {/* Q7 */}
          <div className="space-y-2">
            <label className={labelCls}>Q7. If we let go, why? *</label>
            <textarea value={formData.q7_why_let_go} onChange={e => set('q7_why_let_go', e.target.value)} className={inputCls} rows={2} placeholder="Explain the reason" data-testid="lord-q7" />
          </div>

          {/* Q8 */}
          <div className="space-y-2">
            <label className={labelCls}>Q8. Was the order finalized without our notice? *</label>
            <div className={radioGroupCls}>
              {['Yes', 'No', 'Partially'].map(o => (
                <RadioOption key={o} name="q8_finalized_without_notice" value={o} label={o} selected={formData.q8_finalized_without_notice} onChange={set} />
              ))}
            </div>
            {formData.q8_finalized_without_notice === 'Yes' && (
              <input value={formData.q8_reason} onChange={e => set('q8_reason', e.target.value)} className={inputCls} placeholder="Reason (optional)" />
            )}
          </div>

          {/* Q9 */}
          <div className="space-y-2">
            <label className={labelCls}>Q9. How strong was the customer relationship? *</label>
            <div className={radioGroupCls}>
              {['Very Strong', 'Strong', 'Average', 'Weak', 'No Relationship'].map(o => (
                <RadioOption key={o} name="q9_relationship_strength" value={o} label={o} selected={formData.q9_relationship_strength} onChange={set} />
              ))}
            </div>
          </div>

          {/* Q10 */}
          <div className="space-y-2">
            <label className={labelCls}>Q10. Areas for improvement (select all that apply)</label>
            <div className="flex flex-wrap gap-2">
              {['Pricing Strategy', 'Product Range', 'Delivery Speed', 'After-Sales Service', 'Customer Engagement', 'Technical Support', 'Brand Visibility', 'Relationship Building'].map(area => (
                <button key={area} type="button" onClick={() => toggleImprovement(area)}
                  className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition ${formData.q10_improvement_areas.includes(area) ? 'border-[#00a69c] bg-[#E2F3F0] text-[#00a69c]' : 'border-[#D5E8E4] text-[#3B5A54] hover:border-[#00a69c]'}`}
                  data-testid={`lord-q10-${area.toLowerCase().replace(/\s/g, '-')}`}>
                  {area}
                </button>
              ))}
            </div>
            <input value={formData.q10_suggestions} onChange={e => set('q10_suggestions', e.target.value)} className={inputCls} placeholder="Additional suggestions (optional)" />
          </div>
        </div>
      )}

      {/* SECTION C: Salesperson Reflection */}
      {step === 3 && (
        <div className="medical-card p-6 space-y-5" data-testid="lord-section-c">
          <h2 className="text-xl font-bold text-[#052A23] border-b border-[#D5E8E4] pb-3" style={{ fontFamily: 'Outfit' }}>Section C: Salesperson Reflection</h2>
          <p className="text-sm text-[#6D8E87]">This section will be reviewed by your reporting manager before the record is finalized.</p>

          <div>
            <label className={labelCls}>Key Reason for Loss *</label>
            <textarea value={formData.key_reason_for_loss} onChange={e => set('key_reason_for_loss', e.target.value)} className={inputCls} rows={3} placeholder="In your own words, what was the single biggest reason we lost this order?" data-testid="lord-key-reason" />
          </div>

          <div>
            <label className={labelCls}>What would you do differently next time? *</label>
            <textarea value={formData.what_differently_next_time} onChange={e => set('what_differently_next_time', e.target.value)} className={inputCls} rows={3} placeholder="Looking back, what changes would you make in your approach?" data-testid="lord-next-time" />
          </div>

          {/* Summary Card */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h4 className="text-sm font-semibold text-red-700 mb-2">Review Summary</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div><span className="text-[#6D8E87]">Lost to:</span> <span className="font-medium">{formData.q1_order_went_to}</span></div>
              <div><span className="text-[#6D8E87]">Reason:</span> <span className="font-medium">{formData.q2_why_chose_them}</span></div>
              <div><span className="text-[#6D8E87]">Pricing:</span> <span className="font-medium">{formData.q3_pricing_competitiveness}</span></div>
              <div><span className="text-[#6D8E87]">Relationship:</span> <span className="font-medium">{formData.q9_relationship_strength}</span></div>
            </div>
          </div>

          <p className="text-xs text-amber-600 font-medium">After submission, this LORD form will be sent to your reporting manager for review and comments.</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-3 mt-6" data-testid="lord-nav-buttons">
        {step > 1 && (
          <button onClick={() => setStep(step - 1)} className="px-8 py-3 border border-[#D5E8E4] rounded-xl font-medium hover:bg-gray-50 transition" data-testid="lord-prev-btn">
            Previous
          </button>
        )}
        <div className="flex-1" />
        {step < 3 ? (
          <button onClick={() => setStep(step + 1)} disabled={!canProceed()}
            className="px-8 py-3 bg-[#00a69c] text-white rounded-xl font-semibold hover:bg-[#00bfb3] transition disabled:opacity-40 disabled:cursor-not-allowed" data-testid="lord-next-btn">
            Next: Section {String.fromCharCode(65 + step)}
          </button>
        ) : (
          <button onClick={handleSubmit} disabled={submitting || !canProceed()}
            className="px-8 py-3 bg-red-500 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-red-600 transition disabled:opacity-40" data-testid="lord-submit-btn">
            <PaperPlaneTilt size={20} weight="bold" />
            {submitting ? 'Submitting...' : 'Submit LORD Form'}
          </button>
        )}
      </div>
    </div>
  );
};

export default LORDForm;
